import React from "react";
import * as Sentry from "@sentry/react";
import MaritacaLogo from "../../static/images/maritaca_logo.png";
import BackgroundImage from "../../static/images/maritaca_elementos_final_RGB-30.png";

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  message?: string;
};

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
    message: undefined,
  };

  componentDidMount(): void {
    window.addEventListener("error", this.handleWindowError);
    window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  componentWillUnmount(): void {
    window.removeEventListener("error", this.handleWindowError);
    window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, message: error?.message };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // React render crashes are NOT auto-captured by Sentry's global
    // handlers — React swallows them and routes here. Report explicitly so
    // the "Nossa equipe de desenvolvimento já foi avisada" message is true.
    // No `handled` tag -> this is a real crash and keeps its Session Replay.
    Sentry.captureException(error, {
      level: "error",
      tags: { boundary: "react-render" },
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  handleWindowError = (event: ErrorEvent) => {
    // Browsers report ResizeObserver delivery deferrals through window.onerror,
    // even though observation continues on the next frame. This is recoverable
    // and must never replace the whole application with the crash fallback.
    if (/^ResizeObserver loop (?:completed with undelivered notifications\.?|limit exceeded)$/i.test(event.message || "")) {
      event.preventDefault();
      return;
    }
    console.error("[ErrorBoundary] Window error:", event.message, event.filename, event.lineno);
    // Guard against re-entrant window errors. On iOS Safari, background
    // failures from third-party SDKs (Auth0, Sentry replay) fire a storm of
    // window error events. Before this guard, each event called
    // setState({ hasError: true }) → re-render of the fallback → another
    // window error during render → setState → … The recursion overflowed
    // the JS call stack ("Maximum call stack size exceeded") and WebKit
    // killed the page ("Could not open page"). See CHAT-MARITACA-10.
    if (this.state.hasError) return;
    this.setState({ hasError: true, message: event.message });
  };

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    event.preventDefault();
    const reason: any = event.reason;
    const message =
      (reason && reason.message) ||
      (typeof reason === "string" ? reason : "Unhandled promise rejection");

    console.error("[ErrorBoundary] Unhandled rejection:", reason);
    // Do not crash the page for unhandled promise rejections
    // Only React render errors (getDerivedStateFromError) should crash the UI
  };

  handleReload = () => {
    this.setState({ hasError: false, message: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.page}>
          <div style={styles.gradientOverlay} />
          <img src={BackgroundImage} alt="" style={styles.backgroundImage} />
          <div style={styles.card}>
            <div style={styles.logoWrap}>
              <img src={MaritacaLogo} alt="Maritaca AI" style={styles.logo} />
            </div>
            <h1 style={styles.title}>Ops, algo inesperado aconteceu</h1>
            <p style={styles.subtitle}>
              Nossa equipe de desenvolvimento já foi avisada.
              Recarregue a página para continuar.
            </p>
            <button onClick={this.handleReload} style={styles.primaryButton}>
              Recarregar
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f8fafc",
    position: "relative",
    overflow: "hidden",
    padding: "24px",
    fontFamily: "'Lexend', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    boxSizing: "border-box",
  },
  gradientOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(248,250,252,0.95) 100%), radial-gradient(circle at 20% 80%, rgba(29,39,82,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59,130,246,0.05) 0%, transparent 50%)",
    zIndex: 0,
  },
  backgroundImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: 0.08,
    zIndex: 0,
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    zIndex: 1,
    background: "#ffffff",
    borderRadius: "20px",
    padding: "32px",
    width: "100%",
    maxWidth: "460px",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e2e8f0",
    textAlign: "center",
  },
  logoWrap: {
    width: "88px",
    height: "88px",
    margin: "0 auto 16px",
    display: "grid",
    placeItems: "center",
    position: "relative",
  },
  logo: {
    width: "82px",
    height: "82px",
    filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.05))",
  },
  title: {
    margin: "0 0 12px",
    fontSize: "22px",
    fontWeight: 700,
    color: "#1e293b",
  },
  subtitle: {
    margin: "0 0 20px",
    color: "#475569",
    lineHeight: 1.5,
    fontSize: "15px",
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#22c55e",
    color: "#0f172a",
    border: "none",
    padding: "12px 16px",
    borderRadius: "10px",
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
    maxWidth: "220px",
    margin: "0 auto",
    fontSize: "15px",
    boxShadow: "0 10px 20px -10px rgba(34,197,94,0.55)",
    transition: "transform 0.15s ease, box-shadow 0.2s ease",
  },
};
